// Inline "search or create a guardian" box for Add Student — the single
// biggest friction point in admitting a student with a brand-new parent
// used to be a trip to a separate tab. Typing a name/phone searches
// existing guardians; "+ New guardian" expands a two-field form (name,
// phone — nothing else required) and creates one without leaving this page.
import { useEffect, useState } from 'react';
import { Search, X, UserPlus, Copy, Check } from 'lucide-react';
import { searchGuardians, createGuardian } from '../api/client';
import { inputCls, Msg, errText } from '../ui/Form';
import Button from '../ui/Button';

export default function GuardianPicker({ value, onChange, onCreated }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    const t = setTimeout(() => searchGuardians(term).then(setResults).catch(() => setResults([])), 250);
    return () => clearTimeout(t);
  }, [q]);

  async function saveNewGuardian(e) {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      const g = await createGuardian({ name: newName.trim(), phone: newPhone.trim() });
      onChange(g);
      onCreated?.(g);
      setCreating(false); setNewName(''); setNewPhone('');
    } catch (err) {
      setMsg(errText(err));
    } finally {
      setSaving(false);
    }
  }

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 border border-transit rounded-s px-3 py-2 bg-transit-tint">
        <span className="text-sm">
          <b className="text-ink">{value.name}</b>
          <span className="text-ink-soft"> &middot; {value.phone}</span>
          {value.latitude == null && <span className="text-caution font-semibold"> &middot; no location set</span>}
        </span>
        <button type="button" onClick={() => onChange(null)} aria-label="Clear guardian" className="text-ink-soft hover:text-ink">
          <X size={15} />
        </button>
      </div>
    );
  }

  if (creating) {
    return (
      <div className="border border-line rounded-s p-3 bg-paper flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-ink-soft">New guardian</span>
          <button type="button" onClick={() => setCreating(false)} aria-label="Cancel" className="text-ink-soft hover:text-ink">
            <X size={14} />
          </button>
        </div>
        <input className={inputCls} placeholder="Full name" value={newName} onChange={e => setNewName(e.target.value)} />
        <input className={inputCls} placeholder="Phone (becomes their app login)" value={newPhone} onChange={e => setNewPhone(e.target.value)} />
        <p className="text-[11px] text-ink-soft -mt-1">A login password is generated automatically and shown once you save.</p>
        <Button type="button" className="self-start" disabled={!newName.trim() || !newPhone.trim() || saving} onClick={saveNewGuardian}>
          {saving ? 'Adding…' : 'Add guardian'}
        </Button>
        <Msg msg={msg} />
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-soft" />
        <input className={`${inputCls} w-full pl-8`} value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search guardian by name or phone…" aria-label="Find guardian" />
      </div>
      {(results.length > 0 || q.trim().length >= 2) && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-paper-raised border border-line rounded-m shadow-2 z-[1300] max-h-64 overflow-y-auto">
          {results.map(g => (
            <button type="button" key={g.id} onClick={() => { onChange(g); setQ(''); setResults([]); }}
              className="w-full text-left px-3 py-2 border-t border-line-soft first:border-t-0 hover:bg-transit-tint text-sm">
              <b className="text-ink">{g.name}</b>
              <span className="text-ink-soft"> &middot; {g.phone}</span>
            </button>
          ))}
          <button type="button" onClick={() => { setCreating(true); setNewPhone(/^\d+$/.test(q.trim()) ? q.trim() : ''); setNewName(/^\d+$/.test(q.trim()) ? '' : q.trim()); setQ(''); setResults([]); }}
            className="w-full flex items-center gap-1.5 text-left px-3 py-2 border-t border-line-soft text-sm font-semibold text-transit-deep hover:bg-transit-tint">
            <UserPlus size={14} /> + New guardian{q.trim() ? ` "${q.trim()}"` : ''}
          </button>
        </div>
      )}
    </div>
  );
}

// Small dismissible banner shown right after an inline guardian creation —
// the generated password needs to reach the office once, clearly, before
// it scrolls off screen.
export function GeneratedPasswordBanner({ guardian, onDismiss }) {
  const [copied, setCopied] = useState(false);
  if (!guardian?.generated_password) return null;
  return (
    <div className="flex items-center justify-between gap-3 border border-transit rounded-s px-3 py-2 bg-transit-tint text-sm">
      <span>
        <b className="text-ink">{guardian.name}</b>'s parent app login — phone <b>{guardian.phone}</b>, password{' '}
        <code className="bg-paper-raised px-1.5 py-0.5 rounded-s font-bold">{guardian.generated_password}</code>
      </span>
      <div className="flex items-center gap-1 shrink-0">
        <button type="button" onClick={() => { navigator.clipboard.writeText(guardian.generated_password); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          className="text-transit-deep hover:text-ink p-1" aria-label="Copy password">
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
        <button type="button" onClick={onDismiss} className="text-ink-soft hover:text-ink p-1" aria-label="Dismiss"><X size={14} /></button>
      </div>
    </div>
  );
}
